/**
 * Calendar — the time grid over four subsystems (docs/44).
 *
 * Draws commitment occurrences, imported events, task blocks and logged time
 * as positioned blocks on a Day / 3-Day / Week grid, in the ACCOUNT timezone.
 * The page never computes a local day, an hour label, or a DST boundary: all
 * of that arrives from GET /api/calendar/range as `days[]` (true bounds +
 * hour marks) and the pure layout core in ../calendar/layout positions blocks
 * as fractions of each day's real length. The browser's zone appears only in
 * two honest places — the traveling notice comparing it to the account zone,
 * and the quick-create dialog's local datetime inputs.
 *
 * The three-object taxonomy is load-bearing: each kind renders distinctly and
 * a block's buttons come from its server-stated `actions[]`, never guessed.
 * View settings live in React state initialised from CALENDAR_DISPLAY_DEFAULTS
 * until the `user_settings.calendar_display` namespace lands (see that module).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import CollapsibleFilterBar from '../components/CollapsibleFilterBar';
import CalendarMetricInfo from '../components/CalendarMetricInfo';
import OverlapConflictDialog, {
  OverlapEntrySummary,
} from '../components/OverlapConflictDialog';
import PageShell from '../components/PageShell';
import { zonesDiffer } from '../components/HourClassesSection';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';
import CalendarGrid from '../calendar/CalendarGrid';
import type { DayRender } from '../calendar/CalendarGrid';
import {
  CALENDAR_DISPLAY_DEFAULTS,
  CALENDAR_LAYER_KEYS,
  CALENDAR_LAYER_LABELS,
  CalendarDisplaySettings,
  CalendarLayerKey,
  CalendarViewMode,
  LAYER_SETTING_KEYS,
  VIEW_MODE_DAYS,
} from '../calendar/calendarDisplayDefaults';
import {
  groupIntoWeeks,
  orderDays,
  packDay,
  packDayDual,
  resolveWindowEdges,
  splitAcrossDays,
} from '../calendar/layout';
import type { DayBounds, LayoutEntry } from '../calendar/layoutTypes';
import {
  ApiCalendarEntry,
  CalendarDayView,
  CalendarRangeResponse,
  toDayView,
  toLayoutEntry,
} from '../calendar/rangeTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DAY_MS = 86_400_000;
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const KIND_TO_LAYER: Record<ApiCalendarEntry['kind'], CalendarLayerKey> = {
  commitment_occurrence: 'commitment_occurrences',
  planned_event: 'planned_events',
  task_block: 'task_blocks',
  time_entry: 'time_entries',
};

/** Date-only arithmetic on a YYYY-MM-DD key — UTC noon, so no zone or DST
 * can shift the date. This is calendar math on a label, not local-day math. */
const shiftDateKey = (dateKey: string, days: number): string => {
  const shifted = new Date(Date.parse(`${dateKey}T12:00:00Z`) + days * DAY_MS);
  return shifted.toISOString().slice(0, 10);
};

const dateKeyLabel = (dateKey: string): string => {
  const probe = new Date(`${dateKey}T12:00:00Z`);
  return `${WEEKDAY_LABELS[probe.getUTCDay()]} ${dateKey.slice(8)} ${
    MONTH_LABELS[probe.getUTCMonth()]
  }`;
};

/** The browser's date for the anchor seed only — "today" highlighting comes
 * from the server's day bounds, never from this. */
const browserDateKey = (): string => {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const browserZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

/** An instant as the browser-local "YYYY-MM-DDTHH:MM" a datetime-local wants. */
const toLocalInput = (ms: number): string => {
  const value = new Date(ms);
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
    value.getDate(),
  )}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

/**
 * A popover timestamp, rendered in the ACCOUNT zone the grid is drawn in and
 * labeled with the zone's short name, so the detail card can never silently
 * contradict the axis it floats over (Wave 3 finding). Formatting through
 * Intl's timeZone option is display only — no day boundary is derived from
 * it. Falls back to an explicitly labeled browser-zone face if the zone
 * string is unusable. A null instant is "no end time": the word "running" is
 * reserved for entries that actually run (time entries), decided at the call
 * site.
 */
const formatInstant = (iso: string | null, accountZone: string): string => {
  if (!iso) {
    return 'no end time';
  }
  const value = new Date(iso);
  try {
    return value.toLocaleString(undefined, {
      timeZone: accountZone || undefined,
      timeZoneName: 'short',
    });
  } catch {
    return `${value.toLocaleString()} (browser time)`;
  }
};

const isMissingTimezone = (error: any): boolean =>
  error?.response?.status === 409 &&
  /timezone/i.test(String(error?.response?.data?.detail ?? ''));

interface DetailState {
  anchor: HTMLElement;
  entry: ApiCalendarEntry;
}

interface QuickCreateState {
  open: boolean;
  startLocal: string;
  endLocal: string;
  description: string;
  /**
   * The clicked slot's TRUE instants plus the exact prefill strings they
   * produced. While a field still equals its prefill, submit posts the stored
   * instant — the browser-local round trip is lossy in the repeated fall-back
   * hour (its wall time is ambiguous and Date resolves the occurrence the
   * user did not click; Wave 3 finding). Only a user-edited field is parsed
   * as browser-local wall clock.
   */
  startMs: number;
  endMs: number;
  startPrefill: string;
  endPrefill: string;
}

interface ConflictState {
  entry: { id: number; start_time: string; end_time?: string | null; description?: string | null };
  overlaps: OverlapEntrySummary[];
}

const Calendar: React.FC = () => {
  const navigate = useNavigate();
  const { pageHeaderSx, sectionGapSx, dialogContentPaddingSx } = useLayoutTokens();

  const [settings, setSettings] = useState<CalendarDisplaySettings>(
    CALENDAR_DISPLAY_DEFAULTS,
  );
  const [viewMode, setViewMode] = useState<CalendarViewMode>(
    CALENDAR_DISPLAY_DEFAULTS.default_view,
  );
  const [anchorKey, setAnchorKey] = useState<string>(browserDateKey);
  const [data, setData] = useState<CalendarRangeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timezoneMissing, setTimezoneMissing] = useState(false);
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [quickCreate, setQuickCreate] = useState<QuickCreateState>({
    open: false,
    startLocal: '',
    endLocal: '',
    description: '',
    startMs: 0,
    endMs: 0,
    startPrefill: '',
    endPrefill: '',
  });
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const viewDays = VIEW_MODE_DAYS[viewMode];

  const fetchRange = useCallback(async () => {
    setLoading(true);
    setError(null);
    setTimezoneMissing(false);
    // A padded window around the anchor: the server labels the local days, the
    // client only picks which labelled days to show. Week view needs up to 6
    // days behind the anchor to reach the week's opening day.
    const anchorMs = Date.parse(`${anchorKey}T00:00:00Z`);
    const fromIso = new Date(anchorMs - 7 * DAY_MS).toISOString();
    const toIso = new Date(anchorMs + (viewDays + 8) * DAY_MS).toISOString();
    try {
      const response = await axios.get(`${API_BASE_URL}/api/calendar/range`, {
        params: { from: fromIso, to: toIso },
      });
      setData(response.data as CalendarRangeResponse);
    } catch (err: any) {
      if (isMissingTimezone(err)) {
        setTimezoneMissing(true);
      } else {
        setError(getApiErrorMessage(err, 'Failed to load the calendar'));
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [anchorKey, viewDays]);

  useEffect(() => {
    fetchRange();
  }, [fetchRange]);

  const nowMs = Date.now();

  const dayViews = useMemo(
    () => (data ? data.days.map(toDayView) : []),
    [data],
  );

  // Which labelled days to display, picked by dateKey only.
  const displayViews = useMemo((): CalendarDayView[] => {
    if (dayViews.length === 0) {
      return [];
    }
    const ordered = orderDays(dayViews.map((view) => view.bounds));
    const byKey = new Map(dayViews.map((view) => [view.bounds.dateKey, view]));
    const pick = (bounds: DayBounds[]): CalendarDayView[] =>
      bounds
        .map((b) => byKey.get(b.dateKey))
        .filter((view): view is CalendarDayView => view !== undefined);

    if (viewMode === 'week') {
      const weeks = groupIntoWeeks(ordered, settings.week_start);
      const containing = weeks.find((week) =>
        week.some((b) => b.dateKey === anchorKey),
      );
      if (containing) {
        return pick(containing);
      }
    }
    const startIndex = ordered.findIndex((b) => b.dateKey === anchorKey);
    const from = startIndex === -1 ? 0 : startIndex;
    return pick(ordered.slice(from, from + viewDays));
  }, [dayViews, viewMode, settings.week_start, anchorKey, viewDays]);

  const visibleEntries = useMemo(
    () =>
      (data?.entries ?? []).filter(
        (entry) => settings[LAYER_SETTING_KEYS[KIND_TO_LAYER[entry.kind]]],
      ),
    [data, settings],
  );

  const entriesById = useMemo(
    () => new Map(visibleEntries.map((entry) => [entry.id, entry])),
    [visibleEntries],
  );

  const dayRenders = useMemo((): DayRender[] => {
    const layoutEntries: LayoutEntry[] = visibleEntries.map(toLayoutEntry);
    const bounds = displayViews.map((view) => view.bounds);
    const splits = splitAcrossDays(layoutEntries, bounds, nowMs);
    const windowStart = settings.show_full_day ? null : settings.visible_start_minute;
    const windowEnd = settings.show_full_day ? null : settings.visible_end_minute;
    return displayViews.map((view) => {
      const split = splits.get(view.bounds.dateKey) ?? { grid: [], allDayRail: [] };
      // Wall-clock window edges per day, from that day's own hour marks — a
      // "06:00" window really opens at 06:00 on a 23h or 25h day too.
      const edges = resolveWindowEdges(
        view.bounds,
        view.hourMarks,
        windowStart,
        windowEnd,
      );
      const options = {
        nowMs,
        windowStartMs: edges.startMs,
        windowEndMs: edges.endMs,
      };
      const packed = settings.compare_plan_actual
        ? packDayDual(split.grid, view.bounds, options)
        : packDay(split.grid, view.bounds, options);
      return {
        view,
        split,
        packed,
        // Bands paint into every column their instants intersect (half-open,
        // the _obstacles idiom) — a midnight-spanning band arrives under its
        // OPENING date, so dateKey equality would drop the tail leg that
        // reaches into this day (Wave 3 backend fix 2's frontend half).
        bands: settings.show_hour_classes
          ? (data?.hour_class_bands ?? []).filter((band) => {
              const bandStart = Date.parse(band.start_at);
              const bandEnd = Date.parse(band.end_at);
              return (
                bandEnd > view.bounds.startMs && bandStart < view.bounds.endMs
              );
            })
          : [],
        isToday: nowMs >= view.bounds.startMs && nowMs < view.bounds.endMs,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleEntries, displayViews, settings, data]);

  const hiddenLayerCount = CALENDAR_LAYER_KEYS.filter(
    (layer) => !settings[LAYER_SETTING_KEYS[layer]],
  ).length;

  const accountZone = data?.timezone ?? '';
  const localZone = browserZone();
  const travelling =
    accountZone !== '' && localZone !== '' && zonesDiffer(accountZone, localZone);

  const shiftAnchor = (direction: 1 | -1) =>
    setAnchorKey((key) => shiftDateKey(key, direction * viewDays));

  const openQuickCreate = (_dateKey: string, _label: string, atMs: number) => {
    const startPrefill = toLocalInput(atMs);
    const endPrefill = toLocalInput(atMs + 60 * 60_000);
    setQuickCreate({
      open: true,
      startLocal: startPrefill,
      endLocal: endPrefill,
      description: '',
      startMs: atMs,
      endMs: atMs + 60 * 60_000,
      startPrefill,
      endPrefill,
    });
  };

  const saveQuickCreate = async () => {
    setSaving(true);
    // Untouched fields post the clicked slot's stored instant — lossless in
    // every zone, including the ambiguous fall-back hour. Edited fields are
    // what the widget can express: browser-local wall clock.
    const startIso =
      quickCreate.startLocal === quickCreate.startPrefill
        ? new Date(quickCreate.startMs).toISOString()
        : new Date(quickCreate.startLocal).toISOString();
    const endIso =
      quickCreate.endLocal === quickCreate.endPrefill
        ? new Date(quickCreate.endMs).toISOString()
        : new Date(quickCreate.endLocal).toISOString();
    try {
      const response = await axios.post(`${API_BASE_URL}/api/time-entries/`, {
        description: quickCreate.description || null,
        start_time: startIso,
        end_time: endIso,
      });
      setQuickCreate((state) => ({ ...state, open: false }));
      const saved = response.data ?? {};
      const overlaps = (Array.isArray(saved.overlaps) ? saved.overlaps : []).filter(
        (overlap: OverlapEntrySummary) => !overlap.acknowledged,
      );
      if (overlaps.length > 0) {
        setConflict({
          entry: {
            id: saved.id,
            start_time: saved.start_time,
            end_time: saved.end_time,
            description: saved.description,
          },
          overlaps,
        });
      }
      await fetchRange();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to log time'));
    } finally {
      setSaving(false);
    }
  };

  const runBlockAction = async (blockId: number, action: 'keep' | 'release') => {
    setActionBusy(true);
    try {
      await axios.post(`${API_BASE_URL}/api/task-blocks/${blockId}/${action}`);
      setDetail(null);
      await fetchRange();
    } catch (err: any) {
      setError(getApiErrorMessage(err, `Failed to ${action} the block`));
    } finally {
      setActionBusy(false);
    }
  };

  const detailEntry = detail?.entry ?? null;

  return (
    <PageShell maxWidth="wide">
      <Box sx={pageHeaderSx}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          spacing={1}
        >
          <Typography variant="h3" component="h1">
            Calendar
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <IconButton aria-label="previous range" onClick={() => shiftAnchor(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Button size="small" onClick={() => setAnchorKey(browserDateKey())}>
              Today
            </Button>
            <IconButton aria-label="next range" onClick={() => shiftAnchor(1)}>
              <ChevronRightIcon />
            </IconButton>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              aria-label="calendar range"
              onChange={(_, next: CalendarViewMode | null) => {
                if (next) setViewMode(next);
              }}
            >
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="three_day">3 days</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={settings.show_full_day}
                  onChange={(event) =>
                    setSettings((s) => ({ ...s, show_full_day: event.target.checked }))
                  }
                />
              }
              label="Show full day"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={settings.compare_plan_actual}
                  onChange={(event) =>
                    setSettings((s) => ({
                      ...s,
                      compare_plan_actual: event.target.checked,
                    }))
                  }
                />
              }
              label="Compare plan vs actual"
            />
          </Stack>
        </Stack>
      </Box>

      {travelling && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your browser reports {localZone} but your account is set to {accountZone} —
          traveling, or wrong setting? The calendar renders in your account timezone.
          <Box sx={{ mt: 0.5 }}>
            <Button size="small" variant="outlined" onClick={() => navigate('/profile')}>
              Open Profile
            </Button>
          </Box>
        </Alert>
      )}

      {timezoneMissing ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>No account timezone is set</AlertTitle>
          The calendar draws wall-clock days, so without a timezone the server will
          not guess where midnight is. Set it in{' '}
          <strong>Profile → Hour classes → Account timezone</strong>.
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="outlined" onClick={() => navigate('/profile')}>
              Open Profile
            </Button>
          </Box>
        </Alert>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
              <Box sx={{ mt: 0.5 }}>
                <Button size="small" onClick={fetchRange}>
                  Retry
                </Button>
              </Box>
            </Alert>
          )}

          <CollapsibleFilterBar
            panelKey="calendar_layer_bar"
            activeFilterCount={hiddenLayerCount}
            title="Layers"
            itemNoun="layer filter"
          >
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {CALENDAR_LAYER_KEYS.map((layer) => (
                <FormControlLabel
                  key={layer}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(settings[LAYER_SETTING_KEYS[layer]])}
                      onChange={(event) =>
                        setSettings((s) => ({
                          ...s,
                          [LAYER_SETTING_KEYS[layer]]: event.target.checked,
                        }))
                      }
                    />
                  }
                  label={CALENDAR_LAYER_LABELS[layer]}
                />
              ))}
            </Stack>
          </CollapsibleFilterBar>

          {(data?.notices ?? []).map((notice) => (
            <Alert key={notice.code} severity="info" sx={{ mb: 1 }}>
              {notice.message}
            </Alert>
          ))}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            data && (
              <Box sx={sectionGapSx}>
                {/* Day headers with the two locked numbers */}
                <Box sx={{ display: 'flex', width: '100%' }}>
                  <Box sx={{ width: 56, flexShrink: 0 }} />
                  {dayRenders.map((day) => (
                    <Box
                      key={day.view.bounds.dateKey}
                      sx={{ flex: 1, minWidth: 0, px: 0.5, pb: 0.5 }}
                      data-day-header={day.view.bounds.dateKey}
                    >
                      <Typography variant="subtitle2" noWrap>
                        {dateKeyLabel(day.view.bounds.dateKey)}
                        {day.isToday ? ' · today' : ''}
                      </Typography>
                      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" color="text.secondary">
                          Planned {day.view.plannedMinutes}m
                        </Typography>
                        <CalendarMetricInfo metric="calendar_planned_minutes" />
                        <Typography variant="caption" color="text.secondary">
                          · Logged {day.view.loggedMinutes}m
                        </Typography>
                        <CalendarMetricInfo metric="calendar_logged_minutes" />
                      </Stack>
                    </Box>
                  ))}
                </Box>

                <CalendarGrid
                  days={dayRenders}
                  entriesById={entriesById}
                  compare={settings.compare_plan_actual}
                  windowStartMinute={
                    settings.show_full_day ? null : settings.visible_start_minute
                  }
                  windowEndMinute={
                    settings.show_full_day ? null : settings.visible_end_minute
                  }
                  onEntryClick={(anchor, entry) => setDetail({ anchor, entry })}
                  onSlotClick={openQuickCreate}
                />
              </Box>
            )
          )}
        </>
      )}

      {/* Per-kind detail popover; buttons come from the server's actions[]. */}
      <Popover
        open={detail !== null}
        anchorEl={detail?.anchor ?? null}
        onClose={() => setDetail(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {detailEntry && (
          <Box sx={{ p: 2, maxWidth: 380 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {detailEntry.title ||
                (detailEntry.kind === 'time_entry' ? detailEntry.description : null) ||
                'Untitled'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {formatInstant(detailEntry.start_at, accountZone)} →{' '}
              {detailEntry.kind === 'time_entry' && detailEntry.running
                ? 'running'
                : formatInstant(detailEntry.end_at, accountZone)}
            </Typography>

            {detailEntry.kind === 'planned_event' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Imported from {detailEntry.provider}. {detailEntry.read_only_reason}
                </Typography>
              </>
            )}
            {detailEntry.kind === 'commitment_occurrence' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Commitment occurrence · state {detailEntry.state}
                  {detailEntry.reschedulable ? ' · reschedulable' : ''}
                </Typography>
                {detailEntry.actions.includes('respond') && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/commitments')}
                  >
                    Respond in Commitments
                  </Button>
                )}
              </>
            )}
            {detailEntry.kind === 'task_block' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Task block · state {detailEntry.state}
                  {detailEntry.project_title ? ` · ${detailEntry.project_title}` : ''}
                  {' · internal only, never mirrored to Google'}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {detailEntry.actions.includes('keep') && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={actionBusy}
                      onClick={() => runBlockAction(detailEntry.source_id, 'keep')}
                    >
                      Keep
                    </Button>
                  )}
                  {detailEntry.actions.includes('release') && (
                    <Button
                      size="small"
                      disabled={actionBusy}
                      onClick={() => runBlockAction(detailEntry.source_id, 'release')}
                    >
                      Release
                    </Button>
                  )}
                  {detailEntry.actions.includes('move') && (
                    <Button size="small" onClick={() => navigate('/planner')}>
                      Open in Planner
                    </Button>
                  )}
                </Stack>
              </>
            )}
            {detailEntry.kind === 'time_entry' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Logged time · {detailEntry.source}
                  {detailEntry.running ? ' · running' : ''}
                  {detailEntry.expected_end
                    ? ` · expected until ${formatInstant(detailEntry.expected_end, accountZone)}`
                    : ''}
                </Typography>
                {detailEntry.overlap_partner_ids.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Overlaps {detailEntry.overlap_partner_ids.length} other{' '}
                    {detailEntry.overlap_partner_ids.length === 1 ? 'entry' : 'entries'} —{' '}
                    {detailEntry.overlap_acknowledged
                      ? 'acknowledged'
                      : 'not yet acknowledged'}
                  </Typography>
                )}
                {detailEntry.actions.includes('edit') && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/time-entries')}
                  >
                    Open Time Entries
                  </Button>
                )}
              </>
            )}
          </Box>
        )}
      </Popover>

      {/* Quick-create: the one write this page owns (D-P6-3). */}
      <Dialog
        open={quickCreate.open}
        onClose={() => (saving ? undefined : setQuickCreate((s) => ({ ...s, open: false })))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Log time here</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Description"
              size="small"
              value={quickCreate.description}
              onChange={(event) =>
                setQuickCreate((s) => ({ ...s, description: event.target.value }))
              }
            />
            <TextField
              label="Start"
              type="datetime-local"
              size="small"
              value={quickCreate.startLocal}
              onChange={(event) =>
                setQuickCreate((s) => ({ ...s, startLocal: event.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End"
              type="datetime-local"
              size="small"
              value={quickCreate.endLocal}
              onChange={(event) =>
                setQuickCreate((s) => ({ ...s, endLocal: event.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="caption" color="text.secondary">
              Untouched times save the exact slot you clicked; edited times are
              interpreted in your browser's clock. Overlap with existing
              entries warns but never blocks.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setQuickCreate((s) => ({ ...s, open: false }))}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={saveQuickCreate} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Log time'}
          </Button>
        </DialogActions>
      </Dialog>

      <OverlapConflictDialog
        open={conflict !== null}
        entry={conflict?.entry ?? null}
        overlaps={conflict?.overlaps ?? []}
        onClose={() => setConflict(null)}
        onResolved={fetchRange}
      />
    </PageShell>
  );
};

export default Calendar;
